import Text from '@components/Text';
import colors from '@constants/colors';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import CloseInCircleIcon from '@icons/CloseInCircleIcon';
import {
    FocusEvent, ReactNode, TextareaHTMLAttributes, useRef, useState,
} from 'react';

/** 소프트 키보드가 올라온 뒤 스크롤하도록 키보드 애니메이션만큼 기다린다. */
const KEYBOARD_DELAY = 300;

type BaseProps = Omit<TextareaHTMLAttributes<HTMLInputElement|HTMLTextAreaElement>, 'maxLength'> & {
    textAlign?: 'left' | 'center' | 'right';
    value?: string,
    setValue?: (value: string) => void;
    as?: 'input'|'textarea';
    min?: number;
    max?: number;
    trailing?: ReactNode;
    /** 필드 위에 붙는 Title 슬롯. */
    title?: string;
    /** 값이 있을 때 우측에 Clear 버튼을 띄운다. trailing 과 함께 쓰지 않는다. */
    clearable?: boolean;
    helperText?: string;
    errorText?: string;
};

/** 글자 수를 노출하면 상한이 있어야 하므로 maxLength 를 필수로 받는다. */
type LengthProps = {
    showLength: true;
    maxLength: number;
} | {
    showLength?: false;
    maxLength?: number;
};

type Props = BaseProps & LengthProps;

/**
 * React 가 관리하는 value 를 밖에서 바꾸고 onChange 까지 태우기 위해
 * 네이티브 setter 로 값을 넣은 뒤 input 이벤트를 발생시킨다.
 */
function setNativeValue(el: HTMLInputElement | HTMLTextAreaElement, value: string) {
    const proto = el instanceof HTMLTextAreaElement
        ? HTMLTextAreaElement.prototype
        : HTMLInputElement.prototype;
    Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(el, value);
    el.dispatchEvent(new Event('input', { bubbles: true }));
}

function TextField(props: Props) {
    const {
        showLength, textAlign = 'left', setValue,
        value, maxLength, min, max,
        as: Component = 'input',
        onChange, onFocus, trailing, title, clearable, helperText,
        className, errorText, ...rest
    } = props;
    const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
    const [length, setLength] = useState<number>(0);
    // 제어 컴포넌트면 value 가, 아니면 마지막 입력 길이가 기준이 된다.
    const currentLength = value === undefined ? length : value.length;
    const isLengthReached = !!showLength && maxLength !== undefined && currentLength >= maxLength;
    const hasError = !!errorText || isLengthReached;
    const showClear = !!clearable && !trailing && currentLength > 0
        && !rest.disabled && !rest.readOnly;
    const handleFocus = (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const el = e.currentTarget;
        setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), KEYBOARD_DELAY);
        onFocus?.(e);
    };
    const handleClear = () => {
        setValue?.('');
        setLength(0);
        if (inputRef.current) {
            setNativeValue(inputRef.current, '');
            inputRef.current.focus();
        }
    };
    return (
        <Container>
            {title && (
                <Text size='body-long'
                    fontWeight='bold'
                    color={colors.textSecondary} >
                    {title}
                </Text>
            )}
            <Contents>
                <Component css={inputCss(textAlign, Component === 'input', hasError, !!clearable)}
                    className={className}
                    ref={inputRef}
                    value={value}
                    maxLength={maxLength}
                    min={min}
                    max={max}
                    onFocus={handleFocus}
                    onChange={(e) => {
                        // 한글 IME 조합 중에는 브라우저가 maxLength 를 적용하지 않아 직접 자른다.
                        if (maxLength !== undefined && e.target.value.length > maxLength) {
                            e.target.value = e.target.value.slice(0, maxLength);
                        }
                        setValue?.(e.target.value);
                        setLength(e.target.value.length);
                        onChange?.(e);
                    }}
                    {...rest} />
                {showLength && (
                    <Count isTextarea={Component === 'textarea'}
                        hasError={isLengthReached} >
                        {currentLength} /  {maxLength}
                    </Count>
                )}
                {showClear && (
                    <ClearButton type='button'
                        aria-label='clear'
                        onClick={handleClear} >
                        <CloseInCircleIcon size={16}
                            color={colors.gray7} />
                    </ClearButton>
                )}
                {trailing && (
                    <Trailing>{trailing}</Trailing>
                )}
            </Contents>
            {helperText && (
                <Text color={colors.gray6} size='body-long'>{helperText}</Text>
            )}
            {errorText && (
                <Text color={colors.textError} size='body-long'>{errorText}</Text>
            )}
        </Container>
    );
}

const inputCss = (
    textAlign: 'left' | 'center' | 'right',
    isInput: boolean,
    hasError: boolean,
    isClearable: boolean,
) => css`
    background-color: var(--color-background-base);
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-sm);
    color: var(--color-text-primary);
    font-family: PretendardFull, serif;
    font-size: 14px;
    line-height: 20px;
    padding: 10px ${isClearable ? '40px' : '12px'} 10px 12px;
    resize: none;
    width: 100%;
    -webkit-user-select: text;
    caret-color: var(--color-text-primary);
    text-align: ${textAlign};
    ${isInput && 'height: 40px;'}

    &::placeholder{
        color: var(--color-text-unseledcted);
        font-weight: 400;
    }

    &:focus {
        border: 1px solid ${hasError ? 'var(--color-border-error)' : 'var(--color-border-default)'};
        outline: none;
    }

    &:read-only {
        background-color:  var(--color-gray1);
    }

    &:disabled {
        background-color: var(--color-button-disabled);
        border: 1px solid var(--color-border-disabled);
        color: var(--color-text-unseledcted);
    }

    ${hasError && css`
        &, &:focus {
            border: 1px solid var(--color-border-error);
        }
    `}
`;

const Count = styled.span<{
    isTextarea?: boolean;
    hasError?: boolean;
}>`
    color: ${({ hasError }) => (hasError ? colors.textError : 'var(--color-gray8)')};
    font-size: 14px;
    position: absolute;
    right: 20px;
    bottom: ${({ isTextarea }) => (isTextarea ? '20px' : 'unset')};
`;

const Contents = styled.div`
    align-items: center;
    display: flex;
    position: relative;
    width: 100%;
`;

const Container = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    row-gap: 4px;
`;

const Trailing = styled.div`
    position: absolute;
    right: 0;
`;

const ClearButton = styled.button`
    align-items: center;
    background: none;
    border: none;
    display: flex;
    padding: 0;
    position: absolute;
    right: 12px;
`;

export default TextField;
